import { LuCheck, LuPencil, LuX } from 'react-icons/lu'

import { Truncate } from '@/lib/core/components/Truncate'

import { REFERENCE_MAX_LENGTH } from '@/lib/features/SmartShares/constants'

import messages from './EditableReference.messages'
import styles from './EditableReference.module.css'
import { EditableReferenceProps } from './EditableReference.types'
import { canEditReference, formatReferenceForDisplay } from './EditableReference.utils'
import { useEditableReference } from './useEditableReference'

export const EditableReference = ({
  share,
  truncateLimit = 15,
  onUpdated,
  dataTestId = 'editable-reference',
}: EditableReferenceProps) => {
  const { id: shareId, bridgeStatus, reference } = share

  const {
    cancelEditing,
    changeValue,
    error,
    handleKeyDown,
    inputRef,
    isEditing,
    isSaving,
    save,
    startEditing,
    value,
  } = useEditableReference({ share, onUpdated })

  if (!canEditReference(bridgeStatus)) {
    return (
      <span className={styles.value} data-testid={`${dataTestId}-readonly-${shareId}`}>
        <Truncate content={formatReferenceForDisplay(reference)} limit={truncateLimit} />
      </span>
    )
  }

  if (isEditing) {
    return (
      <div className={styles.editor} data-testid={`${dataTestId}-editor-${shareId}`}>
        <div className={styles.editorRow}>
          <input
            ref={inputRef}
            className={styles.input}
            type="text"
            value={value}
            maxLength={REFERENCE_MAX_LENGTH}
            aria-label={messages.inputLabel.defaultMessage}
            aria-invalid={Boolean(error)}
            placeholder={messages.placeholder.defaultMessage}
            disabled={isSaving}
            onChange={({ target }) => changeValue(target.value)}
            onKeyDown={handleKeyDown}
            data-testid={`${dataTestId}-input-${shareId}`}
          />
          <button
            type="button"
            className={styles.iconButton}
            aria-label={messages.save.defaultMessage}
            title={messages.save.defaultMessage}
            disabled={isSaving || Boolean(error)}
            onClick={save}
            data-testid={`${dataTestId}-save-${shareId}`}
          >
            <LuCheck size={16} />
          </button>
          <button
            type="button"
            className={styles.iconButton}
            aria-label={messages.cancel.defaultMessage}
            title={messages.cancel.defaultMessage}
            disabled={isSaving}
            onClick={cancelEditing}
            data-testid={`${dataTestId}-cancel-${shareId}`}
          >
            <LuX size={16} />
          </button>
        </div>
        {error && (
          <span
            className={styles.error}
            role="alert"
            data-testid={`${dataTestId}-error-${shareId}`}
          >
            {error}
          </span>
        )}
      </div>
    )
  }

  return (
    <div className={styles.container} data-testid={`${dataTestId}-${shareId}`}>
      <span className={styles.value}>
        <Truncate content={formatReferenceForDisplay(reference)} limit={truncateLimit} />
      </span>
      <button
        type="button"
        className={styles.iconButton}
        aria-label={messages.edit.defaultMessage}
        title={messages.edit.defaultMessage}
        onClick={startEditing}
        data-testid={`${dataTestId}-edit-${shareId}`}
      >
        <LuPencil size={14} />
      </button>
    </div>
  )
}
